import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { optimizeImage, imageUrls } from '../utils/imageOptimizer';
import SEO from '../components/SEO';

// Original image URLs
const images = {
  hero: 'https://customer-assets.emergentagent.com/job_neon-display-1/artifacts/hfff1vbs_251206_NEONSIGN_CHP-238.jpg',
  bodegaDanza: 'https://customer-assets.emergentagent.com/job_neon-display-1/artifacts/47tdp696_251206_NEONSIGN_CHP-68.jpg',
  killerKebab: 'https://customer-assets.emergentagent.com/job_neon-display-1/artifacts/urnldo1l_251206_NEONSIGN_CHP-133.jpg',
  magasin: 'https://customer-assets.emergentagent.com/job_neon-display-1/artifacts/mlqqmv3b_251206_NEONSIGN_CHP-160.jpg',
  kajen: 'https://customer-assets.emergentagent.com/job_neon-display-1/artifacts/08w4zlmg_251206_NEONSIGN_CHP-197.jpg',
};

// Kundelogoer
const clientLogos = [
  { name: "Magasin du Nord", src: "/logos/magasin.svg" },
  { name: "Politiken", src: "/logos/politiken.svg" },
  { name: "Matas", src: "/logos/matas.svg" },
  { name: "Cabinn", src: "/logos/cabinn.svg" },
  { name: "Jensens Bøfhus", src: "/logos/jensens.svg" },
  { name: "Molslinjen", src: "/logos/molslinjen.svg" },
];

// USP'er fra dokumentet
const usps = [
  {
    title: 'Kreativitet & Design',
    description: 'Kendt for æstetik, ægte neon og løsninger med et stærkt visuelt udtryk.',
  },
  {
    title: 'Proces & Sikkerhed',
    description: 'Vi gør komplekse opgaver simple – og leverer præcist som aftalt.',
  },
  {
    title: 'Relation & Integritet',
    description: 'Vi bygger relationen med samme integritet, som vi bygger løsningen.',
  },
];

export default function Home() {
  return (
    <main data-testid="home-page">
      <SEO 
        pathname="/"
        description="NeonSign er Danmarks førende leverandør af skiltning, wayfinding og facadeløsninger. +30 års erfaring med komplekse projekter for brands som Matas, Politiken og Magasin."
      />
      {/* Hero Section - Dark, full screen */}
      <section 
        data-testid="hero-section"
        className="relative h-screen flex items-end"
      >
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src={imageUrls.hero(images.hero)}
            alt="Neonsign skiltning"
            className="w-full h-full object-cover"
            loading="eager"
            fetchpriority="high"
          />
          <div className="hero-gradient absolute inset-0" />
        </div>

        {/* Content */}
        <div className="relative z-10 w-full pb-20 md:pb-32">
          <div className="max-w-7xl mx-auto px-6 md:px-8">
            <div className="max-w-3xl">
              <p className="text-white/60 text-xs font-medium tracking-[0.2em] uppercase mb-6 animate-fade-in">
                Professionelle skilte siden 1994
              </p>
              <h1 
                data-testid="hero-title"
                className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.1] text-white mb-6 animate-fade-in-up"
              >
                Vi gør komplekse skilteløsninger simple – og leverer præcist som aftalt
              </h1>
              <p className="text-white/70 text-lg md:text-xl mb-10 animate-fade-in-up stagger-2 max-w-2xl">
                Vi er partneren, der tager ansvar hele vejen fra idé og design til produktion og montering. For arkitekter, designere og virksomheder, hvor løsningen ikke bare skal se rigtig ud – men fungere i praksis.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up stagger-3">
                <Link
                  to="/kontakt"
                  data-testid="hero-cta-contact"
                  className="btn-light inline-flex items-center justify-center gap-2"
                >
                  Kontakt os
                </Link>
                <Link
                  to="/cases"
                  data-testid="hero-cta-cases"
                  className="text-white/90 hover:text-white text-sm font-medium tracking-[0.1em] uppercase inline-flex items-center gap-2 transition-colors"
                >
                  Se cases
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
          <div className="w-[1px] h-16 bg-gradient-to-b from-transparent via-white/50 to-transparent" />
        </div>
      </section>

      {/* USP Section - Light */}
      <section className="bg-white py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
            {usps.map((usp, index) => (
              <div key={usp.title} className="text-center md:text-left">
                <h3 className="font-heading text-lg font-medium text-[#1A1A1A] mb-3">
                  {usp.title}
                </h3>
                <p className="text-[#666666] leading-relaxed">
                  {usp.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Three Image Section - All visible in one frame on desktop */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-0">
        <Link to="/loesninger#neon" className="relative aspect-[4/5] md:aspect-[3/4] lg:aspect-square image-hover block group">
          <img
            src={imageUrls.large(images.bodegaDanza)}
            alt="Neonskilte"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-500" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
            <p className="text-xs tracking-[0.2em] uppercase mb-2 opacity-80">Neonskilte</p>
            <p className="text-base md:text-lg font-light tracking-wide">Ægte neon i høj kvalitet – produceret i Europa</p>
          </div>
        </Link>
        <Link to="/loesninger#led" className="relative aspect-[4/5] md:aspect-[3/4] lg:aspect-square image-hover block group">
          <img
            src={imageUrls.large(images.killerKebab)}
            alt="LED-skilte"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-500" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
            <p className="text-xs tracking-[0.2em] uppercase mb-2 opacity-80">LED-skilte</p>
            <p className="text-base md:text-lg font-light tracking-wide">Energieffektive løsninger til moderne skiltning</p>
          </div>
        </Link>
        <Link to="/loesninger#facade" className="relative aspect-[4/5] md:aspect-[3/4] lg:aspect-square image-hover block group overflow-hidden bg-black">
          <video
            src="https://customer-assets.emergentagent.com/job_82f36689-5905-4eb9-a410-52f80a0daaf2/artifacts/sfgq36wx_251125_NEONSIGN_AARHUS.mp4"
            poster="https://customer-assets.emergentagent.com/job_82f36689-5905-4eb9-a410-52f80a0daaf2/artifacts/6abk1a5g_251125_NeonSign_aarhus_m3--11.jpg"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-500" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
            <p className="text-xs tracking-[0.2em] uppercase mb-2 opacity-80">Facadeskilte</p>
            <p className="text-base md:text-lg font-light tracking-wide">Skilteløsninger hvor synlighed og udtryk er afgørende</p>
          </div>
        </Link>
      </section>

      {/* Light Section - About */}
      <section className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#999999] text-xs tracking-[0.2em] uppercase mb-6">
                Om NeonSign
              </p>
              <h2 className="font-heading text-3xl md:text-4xl font-light text-[#1A1A1A] mb-6 leading-tight">
                Partneren til projekter, hvor skiltning skal fungere i praksis
              </h2>
              <p className="text-[#666666] leading-relaxed mb-8">
                Hos Neonsign leverer vi skilteløsninger og wayfinding til kæder, hoteller og offentlige aktører med komplekse behov. Vi tager ansvar for hele processen – fra idé og design til produktion og montering. Med fokus på struktur, kvalitet og leverancesikkerhed sikrer vi, at løsningen ikke bare ser rigtig ud, men fungerer i praksis og bliver leveret præcist som aftalt.
              </p>
              <Link
                to="/om-os"
                className="btn-outline inline-flex items-center gap-2"
              >
                Læs mere om os
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-8">
              <div>
                <p className="stat-number">30+</p>
                <p className="text-[#999999] text-sm mt-2">Års erfaring</p>
              </div>
              <div>
                <p className="stat-number">300+</p>
                <p className="text-[#999999] text-sm mt-2">Projekter</p>
              </div>
              <div>
                <p className="stat-number">100+</p>
                <p className="text-[#999999] text-sm mt-2">Lokationer</p>
              </div>
              <div>
                <p className="stat-number">50+</p>
                <p className="text-[#999999] text-sm mt-2">Faste kunder</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Another Full Width Image */}
      <section className="relative h-[70vh] image-hover">
        <img
          src={imageUrls.hero('https://customer-assets.emergentagent.com/job_82f36689-5905-4eb9-a410-52f80a0daaf2/artifacts/k85en2og_251206_NEONSIGN_CHP-9.jpg')}
          alt="Kødbyen"
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
          <div className="max-w-7xl mx-auto">
            <p className="text-white/60 text-xs tracking-[0.2em] uppercase mb-4">Neon</p>
            <h2 className="font-heading text-3xl md:text-4xl font-light text-white mb-6">Kødbyen</h2>
            <Link to="/cases/3" className="btn-light inline-flex items-center gap-2">
              Se projektet
            </Link>
          </div>
        </div>
      </section>

      {/* Light Section - Services */}
      <section className="bg-[#F8F8F8] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-16">
            <p className="text-[#999999] text-xs tracking-[0.2em] uppercase mb-6">
              Vores løsninger
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-light text-[#1A1A1A] mb-4">
              Skiltning og wayfinding til projekter, hvor det skal fungere i praksis
            </h2>
            <p className="text-[#666666] max-w-2xl mx-auto">
              Vi forener branding, skiltning og systematisk wayfinding i én samlet leverance – fra idé og design til produktion og montering.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 text-center">
            {[
              { name: 'Neonskilte', id: 'neon' },
              { name: 'LED-skilte', id: 'led' },
              { name: 'Facadeskilte', id: 'facade' },
              { name: 'Pyloner', id: 'pyloner' },
              { name: 'Tagreklamer', id: 'tagreklamer' },
              { name: 'LED-skærme', id: 'led-displays' },
            ].map((service) => (
              <Link
                key={service.id}
                to={`/loesninger#${service.id}`}
                className="group"
              >
                <p className="text-sm text-[#666666] group-hover:text-[#1A1A1A] transition-colors duration-300">
                  {service.name}
                </p>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/loesninger" className="btn-outline inline-flex items-center gap-2">
              Se alle løsninger
            </Link>
          </div>
        </div>
      </section>

      {/* Client Logos */}
      <section className="bg-white py-16 md:py-20 border-t border-[#F0F0F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <p className="text-center text-[#999999] text-xs tracking-[0.2em] uppercase mb-12">
            Udvalgte kunder
          </p>
          <div className="flex flex-wrap justify-center items-center gap-x-12 gap-y-8 md:gap-x-16 lg:gap-x-20">
            {clientLogos.map((logo) => (
              <img
                key={logo.name}
                src={logo.src}
                alt={logo.name}
                className="h-8 md:h-10 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity duration-300"
                loading="lazy"
              />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#1A1A1A] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-light text-white mb-6">
            Har du et projekt, der skal fungere i praksis?
          </h2>
          <p className="text-white/60 mb-10 max-w-lg mx-auto">
            Vi hjælper med at omsætte idéer til skiltning og wayfinding, der bliver leveret, implementeret og fungerer som en del af projektet. Tag fat i os – så tager vi ansvar for næste skridt.
          </p>
          <Link to="/kontakt" className="btn-light inline-flex items-center gap-2">
            Kontakt os
          </Link>
        </div>
      </section>
    </main>
  );
}
